import { Platform } from 'react-native';
import SimpleStorage from './simpleStorage';
import ProductionStorage from './productionStorage';
import { UserRegistrationService, UserRegistration } from '../services/userRegistrationService';
import type { CustomerQueueVisibility } from '../types/customerQueueVisibility';
import { sanitizeForFirestore } from '../services/unifiedAuthService';
import { normalizeBusinessCategoryFields } from '../constants/businessCategories';

export interface User {
  id: string;
  name: string;
  username: string;
  password: string;
  phone: string;
  email: string;
  address: string | {
    street?: string;
    city?: string;
    state?: string;
    zipcode?: string;
    country?: string;
  };
  userType: 'customer' | 'business';
  registrationDate: string;
  lastLogin?: string;
}

export interface BusinessHours {
  [day: string]: {
    isOpen: boolean;
    openTime?: string; // Format: "HH:mm" (e.g., "09:00")
    closeTime?: string; // Format: "HH:mm" (e.g., "17:00")
  };
}

export interface BusinessOwner extends User {
  companyName: string;
  /** Canonical category id from BUSINESS_CATEGORIES (e.g. automotive). */
  categoryId?: string;
  industry: string;
  companySize: string;
  website?: string;
  /** Optional business description (some flows store it on the owner profile). */
  description?: string;
  username: string;
  password: string;
  trialData?: any;
  addedLocations?: any[];
  addedResources?: any[];
  addedHours?: any;
  businessHours?: BusinessHours; // Hours for all 7 days (Monday-Sunday)
  appointmentDuration?: number; // Duration between appointments in minutes
  /** When true, customers must pay during booking (no "Confirm booking" option). */
  paymentRequiredAtBooking?: boolean;
  /** Stripe Connect Express account id (acct_…); persisted so payment APIs work after server restarts. */
  stripeConnectAccountId?: string;
  /** Off-platform PayPal: PayPal.me or payment link; customers pay seller directly. */
  paypalDirectPaymentUrl?: string;
  /** Off-platform Stripe: Payment Link URL; customers pay seller directly. */
  stripeDirectPaymentUrl?: string;
  /** Versioned acceptance of merchant payment terms before Connect Stripe/PayPal. */
  merchantPaymentTermsAcceptance?: {
    version: string;
    acceptedAt: string;
    acceptedItemIds: string[];
    provider?: 'stripe' | 'paypal' | 'both';
  };
  /** Super-admin approval before Stripe Connect onboarding is allowed. */
  stripeConnectApproval?: import('../types/stripeConnectApproval').StripeConnectApprovalRecord;
  /** What customers see for queue / ETA in the app (stored on `businesses/{id}` in Firestore). */
  customerQueueVisibility?: CustomerQueueVisibility;
  /** When true, completed visits show as "Ready for pickup" / "In progress" for customers (pickup-style businesses). */
  pickupReadyDisplayEnabled?: boolean;
  /** When true with pickup display, SMS is sent when staff marks the visit complete. */
  pickupReadySmsEnabled?: boolean;
  /** DoorDash delivery enabled for this merchant (requires Stripe Connect). */
  deliveryEnabled?: boolean;
  deliveryPickupAddress?: string;
  deliveryPickupPhone?: string;
  doordashStoreId?: string;
  /** When true, new Firestore appointments for this business can sync to connected device calendars (while the app is open). */
  calendarAutoSyncEnabled?: boolean;
  /** Local/Firestore write timestamp used to avoid stale background sync overwriting edits. */
  updatedAt?: string;
}

const USER_STORAGE_KEY = 'user'; // ProductionStorage adds 'bookapt_' prefix
const BUSINESS_OWNER_STORAGE_KEY = 'business_owner'; // ProductionStorage adds 'bookapt_' prefix

// Cache to avoid repeated storage reads
let userCache: User | null | undefined = undefined;
let businessOwnerCache: BusinessOwner | null | undefined = undefined;
let businessOwnerLoadInFlight: Promise<BusinessOwner | null> | null = null;
let businessOwnerFirestoreRefreshInFlight: Promise<void> | null = null;
/** Incremented on each local save so in-flight Firestore refresh cannot overwrite newer edits. */
let businessOwnerSaveGeneration = 0;
let firestoreReadDegradedLogged = false;
let migrationChecked = false; // Flag to avoid repeated migration checks

function pickNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function parseUpdatedAtMs(value: unknown): number {
  if (typeof value !== 'string' || !value.trim()) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function mergeBusinessOwnerFromFirestoreDocs(
  userData: Record<string, unknown>,
  businessData: Record<string, unknown>,
): Pick<BusinessOwner, 'categoryId' | 'industry' | 'website'> {
  const normalized = normalizeBusinessCategoryFields(
    pickNonEmptyString(businessData.categoryId, userData.categoryId, businessData.category) || null,
    pickNonEmptyString(businessData.industry, userData.industry) || null,
  );
  return {
    categoryId: normalized.categoryId,
    industry: normalized.industry,
    website: pickNonEmptyString(businessData.website, userData.website),
  };
}

function businessCategoryFieldsForFirestore(ownerData: BusinessOwner) {
  const normalized = normalizeBusinessCategoryFields(
    ownerData.categoryId,
    ownerData.industry,
  );
  const categoryId = normalized.categoryId || ownerData.categoryId || '';
  const industry = normalized.industry || ownerData.industry || 'General';
  return {
    categoryId: categoryId || undefined,
    industry,
    category: categoryId || industry || 'General',
  };
}

const FIRESTORE_READ_TIMEOUT_MS = 8000;
const FIRESTORE_READ_MAX_ATTEMPTS = 1;

function resolveWebFirestoreClient(): {
  firestoreMod: typeof import('firebase/firestore');
  db: any;
} | null {
  try {
    const unifiedAuth = require('../services/unifiedAuthService');
    unifiedAuth.initializeFirebase();
    return unifiedAuth.getWebFirestoreClient?.() ?? null;
  } catch {
    return null;
  }
}

function resolveBusinessOwnerId(ownerData: BusinessOwner): string {
  if (ownerData.id?.trim()) {
    return ownerData.id.trim();
  }
  if (Platform.OS === 'web') {
    try {
      const { getWebAuth, initializeFirebase } = require('../services/unifiedAuthService');
      initializeFirebase();
      return getWebAuth()?.currentUser?.uid?.trim() || '';
    } catch {
      return '';
    }
  }
  return '';
}

export class AuthManager {
  // Save user data (Firestore + local storage)
  static async saveUser(userData: User): Promise<void> {
    try {
      const userWithLogin = {
        ...userData,
        lastLogin: new Date().toISOString()
      };
      
      // Save to local storage (for offline access and fast initial load)
      await ProductionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userWithLogin));
      
      // Update cache immediately to avoid re-reading from storage
      userCache = userWithLogin;
      
      // Also save/update to Firestore (for cross-browser/device access and so profile reload shows updated zipcode)
      if (Platform.OS === 'web') {
        try {
          const client = resolveWebFirestoreClient();
          const { initializeFirebase } = require('../services/unifiedAuthService');
          
          const initialized = initializeFirebase();
          if (!initialized || !client) {
            console.warn('⚠️  AuthManager: Firebase not initialized, skipping Firestore save');
          } else {
            const { doc, setDoc } = client.firestoreMod;
            const db = client.db;
            
            if (db && userData.id) {
              await setDoc(
                doc(db, 'users', userData.id),
                sanitizeForFirestore({
                  ...userWithLogin,
                  updatedAt: new Date().toISOString(),
                }),
                { merge: true }
              );
              console.log(`✅ AuthManager: User data saved to Firestore: ${userData.id}`);
            }
          }
        } catch (firestoreError: any) {
          console.error('❌ AuthManager: Error saving user to Firestore:', firestoreError);
          console.warn('⚠️  User data saved locally but not in Firestore');
        }
      } else {
        // Mobile: update Firestore so getCurrentUser() returns updated profile (e.g. new zipcode)
        try {
          const { initializeFirebase, getMobileFirestore } = await import('../services/unifiedAuthService');
          if (initializeFirebase()) {
            const getDb = getMobileFirestore?.();
            if (getDb && userData.id) {
              const { doc, setDoc } = require('@react-native-firebase/firestore');
              const db = getDb();
              await setDoc(doc(db, 'users', userData.id), sanitizeForFirestore({
                ...userWithLogin,
                updatedAt: new Date().toISOString(),
              }), { merge: true });
              console.log('✅ AuthManager: User data saved to Firestore (mobile)');
            }
          }
        } catch (mobileErr: any) {
          console.warn('⚠️ AuthManager: Mobile Firestore user save failed:', mobileErr?.message);
        }
      }
      
      // Track user registration
      const registrationData: UserRegistration = {
        id: userData.id,
        name: userData.name,
        username: userData.username,
        email: userData.email,
        phone: userData.phone,
        userType: userData.userType,
        registrationDate: userData.registrationDate,
        lastLogin: userWithLogin.lastLogin,
        isActive: true
      };
      
      await UserRegistrationService.addUserRegistration(registrationData);
      
    } catch (error) {
      console.error('❌ AuthManager: Error saving user:', error);
      throw error;
    }
  }

  // Save business owner data (Firestore + local storage)
  static async saveBusinessOwner(ownerData: BusinessOwner): Promise<void> {
    try {
      console.log('AuthManager: Saving business owner data...');
      console.log('AuthManager: Owner data:', ownerData);

      businessOwnerSaveGeneration += 1;
      const updatedAt = new Date().toISOString();
      
      const categoryFields = businessCategoryFieldsForFirestore(ownerData);
      const ownerWithLogin = {
        ...ownerData,
        categoryId: categoryFields.categoryId || ownerData.categoryId,
        industry: categoryFields.industry,
        website: ownerData.website?.trim() || ownerData.website || '',
        lastLogin: updatedAt,
        updatedAt,
      };
      
      console.log('AuthManager: Owner data with login:', ownerWithLogin);
      console.log('AuthManager: JSON stringified data:', JSON.stringify(ownerWithLogin));
      
      // Save to local storage (for offline access and fast initial load)
      await ProductionStorage.setItem(BUSINESS_OWNER_STORAGE_KEY, JSON.stringify(ownerWithLogin));
      businessOwnerCache = ownerWithLogin as BusinessOwner;
      console.log('AuthManager: Business owner data saved to ProductionStorage successfully');
      
      // Build address object once: Settings may send address as a string; Firestore expects { street, city, state, zipcode, country }
      let addressObj: { street: string; city: string; state: string; zipcode: string; country: string };
      if (typeof ownerData.address === 'string') {
        const addrStr = ownerData.address.trim();
        const zipMatch = addrStr.match(/\b(\d{5})(?:-(\d{4}))?\b/); // US zip (5 or 5+4)
        const zipcode = zipMatch ? zipMatch[1] : '';
        addressObj = {
          street: addrStr,
          city: '',
          state: '',
          zipcode,
          country: '',
        };
      } else {
        const addr = ownerData.address as { street?: string; city?: string; state?: string; zipcode?: string; country?: string } | undefined;
        addressObj = {
          street: addr?.street || '',
          city: addr?.city || '',
          state: addr?.state || '',
          zipcode: addr?.zipcode || '',
          country: addr?.country || '',
        };
      }

      // Also save/update to Firestore (for cross-browser/device access and so reload shows updated zipcode)
      if (Platform.OS === 'web') {
        try {
          const client = resolveWebFirestoreClient();
          const { initializeFirebase } = require('../services/unifiedAuthService');
          
          const initialized = initializeFirebase();
          if (!initialized || !client) {
            console.warn('⚠️  AuthManager: Firebase not initialized, skipping Firestore save');
          } else {
            const { doc, setDoc } = client.firestoreMod;
            const db = client.db;
            const ownerId = resolveBusinessOwnerId(ownerData);
            
            if (db && ownerId) {
              const categoryFields = businessCategoryFieldsForFirestore(ownerData);
              const businessPayload = {
                id: ownerId,
                ownerId,
                name: ownerData.companyName || ownerData.name,
                description: ownerData.description || '',
                ...categoryFields,
                address: addressObj,
                phone: ownerData.phone,
                email: ownerData.email,
                website: ownerData.website || '',
                isActive: true,
                isOnBookAPT: true,
                businessHours: ownerData.businessHours,
                appointmentDuration: ownerData.appointmentDuration,
                paymentRequiredAtBooking: !!(ownerData as any).paymentRequiredAtBooking,
                ...(typeof (ownerData as any).stripeConnectAccountId === 'string' &&
                (ownerData as any).stripeConnectAccountId.startsWith('acct_')
                  ? { stripeConnectAccountId: (ownerData as any).stripeConnectAccountId }
                  : {}),
                customerQueueVisibility: ownerData.customerQueueVisibility,
                pickupReadyDisplayEnabled: !!(ownerData as any).pickupReadyDisplayEnabled,
                pickupReadySmsEnabled: !!(ownerData as any).pickupReadySmsEnabled,
                calendarAutoSyncEnabled: !!(ownerData as any).calendarAutoSyncEnabled,
                ...(ownerData.merchantPaymentTermsAcceptance
                  ? { merchantPaymentTermsAcceptance: ownerData.merchantPaymentTermsAcceptance }
                  : {}),
                ...(ownerData.stripeConnectApproval
                  ? { stripeConnectApproval: ownerData.stripeConnectApproval }
                  : {}),
                ...(typeof ownerData.paypalDirectPaymentUrl === 'string'
                  ? { paypalDirectPaymentUrl: ownerData.paypalDirectPaymentUrl }
                  : {}),
                ...(typeof ownerData.stripeDirectPaymentUrl === 'string'
                  ? { stripeDirectPaymentUrl: ownerData.stripeDirectPaymentUrl }
                  : {}),
                updatedAt,
              };

              await setDoc(
                doc(db, 'users', ownerId),
                sanitizeForFirestore({
                  ...ownerWithLogin,
                  id: ownerId,
                }),
                { merge: true }
              );

              await setDoc(
                doc(db, 'businesses', ownerId),
                sanitizeForFirestore({
                  ...businessPayload,
                  updatedAt,
                }),
                { merge: true }
              );

              console.log(`✅ AuthManager: Business owner data saved to Firestore: ${ownerId}`);
            } else if (!ownerId) {
              console.warn('⚠️ AuthManager: Missing business owner id — saved locally only');
            }
          }
        } catch (firestoreError: any) {
          const msg = firestoreError?.message || String(firestoreError);
          console.error('❌ AuthManager: Error saving business owner to Firestore:', firestoreError);
          if (msg.includes('Expected first argument to collection') || msg.includes('CollectionReference')) {
            console.warn('⚠️  AuthManager: Firestore client mismatch — saved locally only');
          } else {
            console.warn('⚠️  Business owner data saved locally but not in Firestore');
          }
        }
      } else {
        // Mobile: update Firestore so next load (e.g. reopening Settings) shows updated zipcode
        try {
          const { initializeFirebase, getMobileFirestore } = await import('../services/unifiedAuthService');
          if (initializeFirebase()) {
            const mobileFirestore = getMobileFirestore?.();
            if (mobileFirestore && ownerData.id) {
              const { doc, setDoc } = require('@react-native-firebase/firestore');
              const db = mobileFirestore();
              const categoryFields = businessCategoryFieldsForFirestore(ownerData);
              const businessPayload = {
                id: ownerData.id,
                ownerId: ownerData.id,
                name: ownerData.companyName || ownerData.name,
                description: ownerData.description || '',
                ...categoryFields,
                address: addressObj,
                phone: ownerData.phone,
                email: ownerData.email,
                website: ownerData.website || '',
                isActive: true,
                isOnBookAPT: true,
                businessHours: ownerData.businessHours,
                appointmentDuration: ownerData.appointmentDuration,
                paymentRequiredAtBooking: !!(ownerData as any).paymentRequiredAtBooking,
                ...(typeof (ownerData as any).stripeConnectAccountId === 'string' &&
                (ownerData as any).stripeConnectAccountId.startsWith('acct_')
                  ? { stripeConnectAccountId: (ownerData as any).stripeConnectAccountId }
                  : {}),
                customerQueueVisibility: ownerData.customerQueueVisibility,
                pickupReadyDisplayEnabled: !!(ownerData as any).pickupReadyDisplayEnabled,
                pickupReadySmsEnabled: !!(ownerData as any).pickupReadySmsEnabled,
                calendarAutoSyncEnabled: !!(ownerData as any).calendarAutoSyncEnabled,
                ...(ownerData.merchantPaymentTermsAcceptance
                  ? { merchantPaymentTermsAcceptance: ownerData.merchantPaymentTermsAcceptance }
                  : {}),
                ...(ownerData.stripeConnectApproval
                  ? { stripeConnectApproval: ownerData.stripeConnectApproval }
                  : {}),
                ...(typeof ownerData.paypalDirectPaymentUrl === 'string'
                  ? { paypalDirectPaymentUrl: ownerData.paypalDirectPaymentUrl }
                  : {}),
                ...(typeof ownerData.stripeDirectPaymentUrl === 'string'
                  ? { stripeDirectPaymentUrl: ownerData.stripeDirectPaymentUrl }
                  : {}),
                updatedAt,
              };
              await setDoc(
                doc(db, 'users', ownerData.id),
                sanitizeForFirestore(ownerWithLogin),
                { merge: true }
              );
              await setDoc(
                doc(db, 'businesses', ownerData.id),
                sanitizeForFirestore({
                  ...businessPayload,
                  updatedAt,
                }),
                { merge: true }
              );
              console.log('✅ AuthManager: Business owner data saved to Firestore (mobile)');
            }
          }
        } catch (mobileFirestoreError: any) {
          console.warn('⚠️ AuthManager: Mobile Firestore save failed:', mobileFirestoreError?.message);
        }
      }
      
      // Track business owner registration
      const registrationData: UserRegistration = {
        id: ownerData.id,
        name: ownerData.name,
        username: ownerData.username,
        email: ownerData.email,
        phone: ownerData.phone,
        userType: ownerData.userType,
        registrationDate: ownerData.registrationDate,
        lastLogin: ownerWithLogin.lastLogin,
        isActive: true
      };
      
      await UserRegistrationService.addUserRegistration(registrationData);
      console.log('AuthManager: Business owner registration tracked successfully');
    } catch (error) {
      console.error('AuthManager: Error saving business owner:', error);
      console.error('AuthManager: Error details:', JSON.stringify(error, null, 2));
      throw error;
    }
  }

  // Get saved user — always use UnifiedAuthService (Auth-aligned profile on web)
  static async getSavedUser(): Promise<User | null> {
    try {
      const { UnifiedAuthService } = require('../services/unifiedAuthService');
      const user = await UnifiedAuthService.getCurrentUser();
      userCache = user ?? null;
      return user;
    } catch (error) {
      console.error('❌ AuthManager.getSavedUser: Error:', error);
      userCache = null;
      return null;
    }
  }
  
  // Clear user cache (call after logout or user update)
  static clearUserCache(): void {
    userCache = undefined;
  }

  static clearBusinessOwnerCache(): void {
    businessOwnerCache = undefined;
  }

  // Get saved business owner (Firestore for current Firebase session only — never another account's cache)
  static async getSavedBusinessOwner(): Promise<BusinessOwner | null> {
    if (businessOwnerLoadInFlight) {
      return businessOwnerLoadInFlight;
    }

    businessOwnerLoadInFlight = this.resolveSavedBusinessOwner();
    try {
      return await businessOwnerLoadInFlight;
    } finally {
      businessOwnerLoadInFlight = null;
    }
  }

  private static async resolveSavedBusinessOwner(): Promise<BusinessOwner | null> {
    try {
      const unifiedAuth = require('../services/unifiedAuthService');
      const { getWebAuth, initializeFirebase, businessOwnerMatchesAuth, enforceAuthIdentityOnProfile } =
        unifiedAuth;

      let firebaseUser: { uid: string; email?: string | null } | null = null;
      if (Platform.OS === 'web') {
        if (!getWebAuth()) {
          initializeFirebase();
        }
        firebaseUser = getWebAuth()?.currentUser ?? null;
      } else {
        try {
          const { getApp } = require('@react-native-firebase/app');
          const { getAuth } = require('@react-native-firebase/auth');
          firebaseUser = getAuth(getApp()).currentUser;
        } catch {
          firebaseUser = null;
        }
      }

      if (!firebaseUser?.uid) {
        console.warn('⚠️ AuthManager: No Firebase user — cannot load business owner');
        return null;
      }

      if (
        businessOwnerCache &&
        businessOwnerMatchesAuth(businessOwnerCache, firebaseUser)
      ) {
        return enforceAuthIdentityOnProfile(businessOwnerCache, firebaseUser) as BusinessOwner;
      }

      // Web: local profile first — Firestore often hangs when firestore.googleapis.com is blocked
      if (Platform.OS === 'web') {
        const localOwner = await this.tryLoadBusinessOwnerFromLocal(
          firebaseUser,
          businessOwnerMatchesAuth,
          enforceAuthIdentityOnProfile
        );
        if (localOwner) {
          businessOwnerCache = localOwner;
          console.log('✅ AuthManager: Loaded business owner from local storage (web, instant)');
          this.scheduleBusinessOwnerFirestoreRefresh(firebaseUser);
          return localOwner;
        }
      }

      const firestoreData =
        Platform.OS === 'web'
          ? await this.loadBusinessOwnerFromFirestore()
          : await this.loadBusinessOwnerFromFirestoreMobile();

      if (firestoreData && businessOwnerMatchesAuth(firestoreData, firebaseUser)) {
        const aligned = enforceAuthIdentityOnProfile(firestoreData, firebaseUser) as BusinessOwner;
        try {
          await ProductionStorage.setItem(BUSINESS_OWNER_STORAGE_KEY, JSON.stringify(aligned));
        } catch {
          /* ignore */
        }
        businessOwnerCache = aligned;
        console.log('✅ AuthManager: Loaded business owner from Firestore for', aligned.email);
        return aligned;
      }

      const localOwner = await this.tryLoadBusinessOwnerFromLocal(
        firebaseUser,
        businessOwnerMatchesAuth,
        enforceAuthIdentityOnProfile
      );
      if (localOwner) {
        businessOwnerCache = localOwner;
        console.log('✅ AuthManager: Loaded business owner from local storage (session match)');
        return localOwner;
      }

      if (Platform.OS === 'web') {
        const authFallback = this.buildBusinessOwnerFromFirebaseAuth(firebaseUser);
        businessOwnerCache = authFallback;
        try {
          await ProductionStorage.setItem(
            BUSINESS_OWNER_STORAGE_KEY,
            JSON.stringify(authFallback)
          );
        } catch {
          /* ignore */
        }
        console.log(
          '✅ AuthManager: Using Firebase Auth profile as business owner (Firestore unavailable)'
        );
        return authFallback;
      }

      businessOwnerCache = null;
      return null;
    } catch (error) {
      console.error('❌ AuthManager: Error getting saved business owner:', error);
      return null;
    }
  }

  private static logFirestoreDegradedOnce(): void {
    if (firestoreReadDegradedLogged) {
      return;
    }
    firestoreReadDegradedLogged = true;
    console.warn(
      '⚠️ AuthManager: Firestore is not responding in this browser. Using saved profile data. ' +
        'If bookings or payments fail, allow firestore.googleapis.com (disable ad blockers for localhost).'
    );
  }

  private static buildBusinessOwnerFromFirebaseAuth(firebaseUser: {
    uid: string;
    email?: string | null;
    displayName?: string | null;
  }): BusinessOwner {
    const email = (firebaseUser.email || '').trim();
    const name =
      firebaseUser.displayName?.trim() || email.split('@')[0] || 'Business';
    return {
      id: firebaseUser.uid,
      name,
      username: email,
      password: '',
      phone: '',
      email,
      address: '',
      userType: 'business',
      companyName: name,
      industry: 'General',
      companySize: '',
      registrationDate: new Date().toISOString(),
    };
  }

  private static async tryLoadBusinessOwnerFromLocal(
    firebaseUser: { uid: string; email?: string | null },
    businessOwnerMatchesAuth: (
      owner: { id?: string; uid?: string; ownerId?: string; email?: string; username?: string },
      user: { uid: string; email?: string | null }
    ) => boolean,
    enforceAuthIdentityOnProfile: (
      profile: User,
      user: { uid: string; email?: string | null; displayName?: string | null }
    ) => User
  ): Promise<BusinessOwner | null> {
    try {
      const localData = await ProductionStorage.getItem(BUSINESS_OWNER_STORAGE_KEY);
      if (!localData) {
        return null;
      }
      const parsed = JSON.parse(localData) as BusinessOwner;
      if (!businessOwnerMatchesAuth(parsed, firebaseUser)) {
        console.warn(
          '⚠️ AuthManager: Discarding stale business_owner cache (wrong account)',
          { cachedEmail: parsed.email, authEmail: firebaseUser.email }
        );
        await ProductionStorage.removeItem(BUSINESS_OWNER_STORAGE_KEY);
        return null;
      }
      return enforceAuthIdentityOnProfile(parsed, firebaseUser) as BusinessOwner;
    } catch (localError) {
      console.warn('⚠️  AuthManager: Error reading business owner from local storage:', localError);
      return null;
    }
  }

  private static scheduleBusinessOwnerFirestoreRefresh(firebaseUser: {
    uid: string;
    email?: string | null;
  }): void {
    if (businessOwnerFirestoreRefreshInFlight) {
      return;
    }
    businessOwnerFirestoreRefreshInFlight = this.refreshBusinessOwnerFromFirestore(
      firebaseUser
    )
      .catch(() => undefined)
      .finally(() => {
        businessOwnerFirestoreRefreshInFlight = null;
      });
  }

  private static async refreshBusinessOwnerFromFirestore(firebaseUser: {
    uid: string;
    email?: string | null;
  }): Promise<void> {
    const refreshStartedGeneration = businessOwnerSaveGeneration;
    const localBeforeRefresh = businessOwnerCache;
    const firestoreData = await this.loadBusinessOwnerFromFirestore();
    if (!firestoreData) {
      return;
    }
    if (refreshStartedGeneration !== businessOwnerSaveGeneration) {
      console.log('AuthManager: Skipping stale Firestore refresh after local save');
      return;
    }
    const { businessOwnerMatchesAuth, enforceAuthIdentityOnProfile } = require(
      '../services/unifiedAuthService'
    );
    if (!businessOwnerMatchesAuth(firestoreData, firebaseUser)) {
      return;
    }
    const localUpdatedAtMs = parseUpdatedAtMs(localBeforeRefresh?.updatedAt);
    const remoteUpdatedAtMs = parseUpdatedAtMs(firestoreData.updatedAt);
    if (localUpdatedAtMs > remoteUpdatedAtMs) {
      console.log('AuthManager: Keeping newer local business owner profile over Firestore refresh');
      return;
    }
    const aligned = enforceAuthIdentityOnProfile(firestoreData, firebaseUser) as BusinessOwner;
    businessOwnerCache = aligned;
    try {
      await ProductionStorage.setItem(BUSINESS_OWNER_STORAGE_KEY, JSON.stringify(aligned));
    } catch {
      /* ignore */
    }
    console.log('✅ AuthManager: Background Firestore sync updated business owner');
  }

  private static async getFirestoreDocWithTimeout(
    collectionName: string,
    docId: string,
    label: string
  ): Promise<{ snap: { exists: () => boolean; data: () => Record<string, unknown> } | null; timedOut: boolean }> {
    const client = resolveWebFirestoreClient();
    if (!client) {
      return { snap: null, timedOut: false };
    }
    const { doc, getDoc } = client.firestoreMod;
    const firestoreDb = client.db;
    for (let attempt = 1; attempt <= FIRESTORE_READ_MAX_ATTEMPTS; attempt++) {
      try {
        const snapPromise = getDoc(doc(firestoreDb, collectionName, docId));
        let timedOut = false;
        const snap = await Promise.race([
          snapPromise,
          new Promise<null>((resolve) => {
            setTimeout(() => {
              timedOut = true;
              this.logFirestoreDegradedOnce();
              console.warn(
                `⚠️ AuthManager: ${label} timeout (${FIRESTORE_READ_TIMEOUT_MS}ms, attempt ${attempt}/${FIRESTORE_READ_MAX_ATTEMPTS})`
              );
              resolve(null);
            }, FIRESTORE_READ_TIMEOUT_MS);
          }),
        ]);
        if (!timedOut && snap) {
          return { snap, timedOut: false };
        }
      } catch (firestoreError: any) {
        this.logFirestoreDegradedOnce();
        console.warn(
          `⚠️ AuthManager: ${label} error:`,
          firestoreError?.code || firestoreError?.message || firestoreError
        );
        return { snap: null, timedOut: false };
      }
      if (attempt < FIRESTORE_READ_MAX_ATTEMPTS) {
        console.log(`AuthManager: Retrying ${label}…`);
      }
    }
    return { snap: null, timedOut: true };
  }
  
  // Load business owner from Firestore (source of truth)
  private static async loadBusinessOwnerFromFirestore(): Promise<BusinessOwner | null> {
    try {
      const unifiedAuthService = require('../services/unifiedAuthService');
      const { initializeFirebase, getWebAuth } = unifiedAuthService;
      
      initializeFirebase();
      
      const client = resolveWebFirestoreClient();
      if (!client) {
        console.warn('⚠️  AuthManager: Firestore client unavailable');
        return null;
      }
      const { doc, setDoc } = client.firestoreMod;
      const webDb = client.db;
      const webAuth = getWebAuth();
      const firebaseUser = webAuth?.currentUser;
      
      if (!firebaseUser || !webDb) {
        console.warn('⚠️  AuthManager: No Firebase user or DB available');
        return null;
      }
      
      try {
        const { enforceAuthIdentityOnProfile } = require('../services/unifiedAuthService');

        try {
          const { authStateReady } = require('firebase/auth');
          await authStateReady(webAuth);
        } catch {
          /* proceed */
        }

        try {
          await firebaseUser.getIdToken();
        } catch {
          /* proceed — getDoc may still work */
        }

        const [userResult, businessResult] = await Promise.all([
          this.getFirestoreDocWithTimeout(
            'users',
            firebaseUser.uid,
            'Business owner user doc'
          ),
          this.getFirestoreDocWithTimeout(
            'businesses',
            firebaseUser.uid,
            'Business doc'
          ),
        ]);

        const userDoc = userResult.snap;
        const businessDoc = businessResult.snap;

        if (userResult.timedOut || businessResult.timedOut) {
          console.warn(
            '⚠️ AuthManager: Firestore read timed out — will use local business_owner cache if available'
          );
          return null;
        }

        if (!userDoc || !userDoc.exists()) {
          console.warn('⚠️  AuthManager: User document not found in Firestore');
          return null;
        }

        const userData = enforceAuthIdentityOnProfile(userDoc.data() as User, firebaseUser);

        let businessData: Record<string, unknown> = {};

        if (businessDoc && businessDoc.exists()) {
          businessData = businessDoc.data();
        } else {
          // If business document doesn't exist, create it from user data
          console.log('⚠️  AuthManager: Business document not found, creating from user data');
          const businessDocData = {
            id: firebaseUser.uid,
            ownerId: firebaseUser.uid,
            name: userData.name || '',
            description: '',
            category: userData.userType === 'business_owner' ? (userData.industry || 'General') : 'General',
            address: userData.address || {},
            phone: userData.phone || '',
            website: userData.website || '',
            isActive: true,
            createdAt: userData.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          await setDoc(doc(webDb, 'businesses', firebaseUser.uid), businessDocData);
          businessData = businessDocData;
        }
        
        const mergedFields = mergeBusinessOwnerFromFirestoreDocs(
          userData as Record<string, unknown>,
          businessData,
        );
        const loadedOwner = enforceAuthIdentityOnProfile(
          {
            ...userData,
            ...businessData,
            companyName: (businessData.name as string) || userData.name,
            ...mergedFields,
            address: businessData.address || userData.address,
            id: firebaseUser.uid,
            updatedAt: pickNonEmptyString(businessData.updatedAt, userData.updatedAt),
          } as BusinessOwner,
          firebaseUser
        ) as BusinessOwner;
        
        console.log('✅ AuthManager: Loaded business owner from Firestore:', loadedOwner.email);
        return loadedOwner;
      } catch (firestoreError: any) {
        console.warn('⚠️  AuthManager: Error reading from Firestore (using local storage if available):', firestoreError);
        return null; // Return null, local storage will be checked by caller
      }
    } catch (error) {
      console.error('❌ AuthManager: Error syncing from Firestore:', error);
      return null;
    }
  }

  // Load business owner from Firestore on mobile (React Native Firebase modular API)
  private static async loadBusinessOwnerFromFirestoreMobile(): Promise<BusinessOwner | null> {
    try {
      const { initializeFirebase } = require('../services/unifiedAuthService');
      initializeFirebase();
      const { getApp } = require('@react-native-firebase/app');
      const { getAuth } = require('@react-native-firebase/auth');
      const { getFirestore, doc, getDoc } = require('@react-native-firebase/firestore');
      const app = getApp();
      const auth = getAuth(app);
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !firebaseUser.uid) {
        console.warn('⚠️  AuthManager: No Firebase user on mobile');
        return null;
      }
      const uid = firebaseUser.uid;
      const db = getFirestore(app);
      const userDocRef = doc(db, 'users', uid);
      const userDocSnap = await getDoc(userDocRef);
      if (!userDocSnap.exists()) {
        console.warn('⚠️  AuthManager: User document not found in Firestore (mobile)');
        return null;
      }
      const userData = userDocSnap.data() || {};
      const businessDocRef = doc(db, 'businesses', uid);
      const businessDocSnap = await getDoc(businessDocRef);
      let businessData: Record<string, any> = {};
      if (businessDocSnap.exists()) {
        businessData = businessDocSnap.data() || {};
      }
      const { enforceAuthIdentityOnProfile } = require('../services/unifiedAuthService');
      const mergedFields = mergeBusinessOwnerFromFirestoreDocs(userData, businessData);
      const loadedOwner = enforceAuthIdentityOnProfile(
        {
          ...userData,
          ...businessData,
          id: uid,
          companyName: businessData.name || userData.name || '',
          ...mergedFields,
          address: businessData.address || userData.address || {},
          updatedAt: pickNonEmptyString(businessData.updatedAt, userData.updatedAt),
        } as BusinessOwner,
        firebaseUser
      ) as BusinessOwner;
      console.log('✅ AuthManager: Loaded business owner from Firestore (mobile):', loadedOwner.email);
      return loadedOwner;
    } catch (error: any) {
      console.warn('⚠️  AuthManager: Error loading business owner from Firestore (mobile):', error?.message || error);
      return null;
    }
  }

  // Login user with credentials
  static async loginUser(username: string, password: string): Promise<User | null> {
    try {
      console.log('🔐 AuthManager.loginUser: Attempting login with:', username);
      
      // Step 1: Check if user is already saved (current session)
      let user = await this.getSavedUser();
      console.log('🔐 AuthManager.loginUser: Step 1 - Retrieved saved user:', user ? 'User found' : 'No user found');
      
      if (user) {
        console.log('🔐 AuthManager.loginUser: User found in saved storage, checking credentials...');
        console.log('🔐 AuthManager.loginUser: User email:', user.email);
        console.log('🔐 AuthManager.loginUser: User username:', user.username);
        console.log('🔐 AuthManager.loginUser: Input username:', username);
        console.log('🔐 AuthManager.loginUser: Email match:', user.email === username);
        console.log('🔐 AuthManager.loginUser: Username match:', user.username === username);
        console.log('🔐 AuthManager.loginUser: Password match:', user.password === password);
        
        const emailMatch = user.email === username;
        const usernameMatch = user.username === username;
        const passwordMatch = user.password === password;
        
        if ((emailMatch || usernameMatch) && passwordMatch) {
          console.log('✅ AuthManager.loginUser: Login successful (from saved user)!');
          // Update last login time
          await this.saveUser(user);
          return user;
        } else {
          console.log('❌ AuthManager.loginUser: Saved user credentials do not match');
          console.log('❌ AuthManager.loginUser: Email match:', emailMatch, 'Username match:', usernameMatch, 'Password match:', passwordMatch);
        }
      }
      
      // Step 2: Check registrations array to find user by email/username
      console.log('🔐 AuthManager.loginUser: Step 2 - Checking user registrations...');
      const registrations = await UserRegistrationService.getAllRegistrations();
      console.log('🔐 AuthManager.loginUser: Found', registrations.length, 'registered users');
      
      // Debug: Check if the user email exists in registrations (case-insensitive)
      const searchEmail = username.toLowerCase().trim();
      console.log('🔐 AuthManager.loginUser: Searching for email:', searchEmail);
      
      // Log last 5 registrations for debugging
      const recentRegs = registrations.slice(-5);
      console.log('🔐 AuthManager.loginUser: Last 5 registered emails:', recentRegs.map(r => r.email));
      
      // Check for exact match first
      let matchingRegistration = registrations.find(
        reg => (reg.email === username || reg.username === username) && reg.isActive
      );
      
      // If not found, try case-insensitive match
      if (!matchingRegistration) {
        console.log('🔐 AuthManager.loginUser: No exact match found, trying case-insensitive...');
        matchingRegistration = registrations.find(
          reg => (reg.email?.toLowerCase() === searchEmail || reg.username?.toLowerCase() === searchEmail) && reg.isActive
        );
      }
      
      if (matchingRegistration) {
        console.log('✅ AuthManager.loginUser: Found matching registration:', matchingRegistration.email);
        console.log('🔐 AuthManager.loginUser: Registration ID:', matchingRegistration.id);
        
        // Step 3: Try to find the full user data (with password) from storage
        // Check all possible storage keys for this user
        console.log('🔐 AuthManager.loginUser: Step 3 - Looking for full user data with password...');
        console.log('🔐 AuthManager.loginUser: Looking for email:', matchingRegistration.email, 'or username:', matchingRegistration.username);
        
        // Try to get from current storage key
        let fullUserData = await ProductionStorage.getItem(USER_STORAGE_KEY);
        if (fullUserData) {
          try {
            const parsed = JSON.parse(fullUserData);
            const emailMatch = parsed.email?.toLowerCase() === matchingRegistration.email?.toLowerCase();
            const usernameMatch = parsed.username?.toLowerCase() === matchingRegistration.username?.toLowerCase();
            console.log('🔐 AuthManager.loginUser: Current storage user:', parsed.email, 'Match:', emailMatch || usernameMatch);
            if (emailMatch || usernameMatch) {
              console.log('✅ AuthManager.loginUser: Found full user data in current storage');
              user = parsed;
            }
          } catch (e) {
            console.warn('AuthManager.loginUser: Error parsing user data:', e);
          }
        }
        
        // If not found, check old storage keys
        if (!user && typeof window !== 'undefined' && window.localStorage) {
          console.log('🔐 AuthManager.loginUser: Checking old storage keys...');
          const oldKeys = ['bookapt_bookapt_user', 'bookapt_user'];
          for (const key of oldKeys) {
            try {
              const oldData = localStorage.getItem(key);
              if (oldData) {
                const parsed = JSON.parse(oldData);
                const emailMatch = parsed.email?.toLowerCase() === matchingRegistration.email?.toLowerCase();
                const usernameMatch = parsed.username?.toLowerCase() === matchingRegistration.username?.toLowerCase();
                console.log('🔐 AuthManager.loginUser: Old key', key, 'user:', parsed.email, 'Match:', emailMatch || usernameMatch);
                if (emailMatch || usernameMatch) {
                  console.log('✅ AuthManager.loginUser: Found full user data in old storage key:', key);
                  user = parsed;
                  // Migrate to new key
                  await ProductionStorage.setItem(USER_STORAGE_KEY, oldData);
                  break;
                }
              }
            } catch (e) {
              console.warn('AuthManager.loginUser: Error checking old key', key, ':', e);
            }
          }
        }
        
        // Also search through ALL localStorage keys that might contain user data
        if (!user && typeof window !== 'undefined' && window.localStorage) {
          console.log('🔐 AuthManager.loginUser: Searching ALL localStorage keys for user data...');
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.includes('user') || key.includes('bookapt'))) {
                try {
                  const data = localStorage.getItem(key);
                  if (data && data.startsWith('{')) {
                    const parsed = JSON.parse(data);
                    if (parsed.email && parsed.password) {
                      const emailMatch = parsed.email?.toLowerCase() === matchingRegistration.email?.toLowerCase();
                      const usernameMatch = parsed.username?.toLowerCase() === matchingRegistration.username?.toLowerCase();
                      if (emailMatch || usernameMatch) {
                        console.log('✅ AuthManager.loginUser: Found user in localStorage key:', key);
                        user = parsed;
                        // Migrate to new key
                        await ProductionStorage.setItem(USER_STORAGE_KEY, data);
                        break;
                      }
                    }
                  }
                } catch (e) {
                  // Skip invalid JSON
                }
              }
            }
          } catch (e) {
            console.warn('AuthManager.loginUser: Error searching localStorage:', e);
          }
        }
        
        // If we found the user data, verify password
        if (user && (user.email === username || user.username === username)) {
          console.log('🔐 AuthManager.loginUser: Verifying password for found user...');
          if (user.password === password) {
            console.log('✅ AuthManager.loginUser: Password verified! Login successful!');
            // Update last login and save
            await this.saveUser(user);
            return user;
          } else {
            console.log('❌ AuthManager.loginUser: Password does not match');
            return null;
          }
        } else {
          console.log('❌ AuthManager.loginUser: Found registration but no full user data with password');
          console.log('❌ AuthManager.loginUser: Cannot verify password - user may need to re-register');
          return null;
        }
      } else {
        console.log('❌ AuthManager.loginUser: No matching registration found for:', username);
        console.log('🔐 AuthManager.loginUser: User not in registrations, but checking all storage for user data anyway...');
        
        // Even if not in registrations, search all storage for user data with matching email/username
        // This handles cases where registration failed but user data exists
        if (typeof window !== 'undefined' && window.localStorage) {
          console.log('🔐 AuthManager.loginUser: Searching ALL localStorage for user:', username);
          let checkedKeys = 0;
          let foundUserData = false;
          
          try {
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.includes('user') || key.includes('bookapt'))) {
                checkedKeys++;
                try {
                  const data = localStorage.getItem(key);
                  if (data && (data.startsWith('{') || data.startsWith('['))) {
                    // Try parsing as object first
                    if (data.startsWith('{')) {
                      const parsed = JSON.parse(data);
                      if (parsed.email && parsed.password) {
                        const emailMatch = parsed.email?.toLowerCase() === username.toLowerCase();
                        const usernameMatch = parsed.username?.toLowerCase() === username.toLowerCase();
                        if (emailMatch || usernameMatch) {
                          foundUserData = true;
                          console.log('✅ AuthManager.loginUser: Found user data in localStorage key:', key);
                          console.log('🔐 AuthManager.loginUser: User email:', parsed.email);
                          console.log('🔐 AuthManager.loginUser: User password exists:', !!parsed.password);
                          // Verify password
                          if (parsed.password === password) {
                            console.log('✅ AuthManager.loginUser: Password verified! Login successful!');
                            // Update last login and save
                            await this.saveUser(parsed);
                            return parsed;
                          } else {
                            console.log('❌ AuthManager.loginUser: Password does not match');
                            console.log('🔐 AuthManager.loginUser: Expected password:', password);
                            console.log('🔐 AuthManager.loginUser: Stored password:', parsed.password);
                            return null;
                          }
                        }
                      }
                    }
                    // Also check if it's an array of users
                    else if (data.startsWith('[')) {
                      const parsed = JSON.parse(data);
                      if (Array.isArray(parsed)) {
                        const foundUser = parsed.find(u => 
                          (u.email?.toLowerCase() === username.toLowerCase() || 
                           u.username?.toLowerCase() === username.toLowerCase()) &&
                          u.password
                        );
                        if (foundUser) {
                          foundUserData = true;
                          console.log('✅ AuthManager.loginUser: Found user in array at key:', key);
                          if (foundUser.password === password) {
                            console.log('✅ AuthManager.loginUser: Password verified! Login successful!');
                            await this.saveUser(foundUser);
                            return foundUser;
                          } else {
                            console.log('❌ AuthManager.loginUser: Password does not match');
                            return null;
                          }
                        }
                      }
                    }
                  }
                } catch (e) {
                  // Skip invalid JSON
                }
              }
            }
            
            console.log(`🔐 AuthManager.loginUser: Comprehensive search complete. Checked ${checkedKeys} keys.`);
            if (!foundUserData) {
              console.log('❌ AuthManager.loginUser: User data NOT found in any localStorage key');
              console.log('❌ AuthManager.loginUser: User may need to register again');
            }
          } catch (e) {
            console.error('❌ AuthManager.loginUser: Error searching localStorage:', e);
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('❌ AuthManager.loginUser: Error logging in user:', error);
      return null;
    }
  }

  // Login business owner with credentials
  static async loginBusinessOwner(username: string, password: string): Promise<BusinessOwner | null> {
    try {
      console.log('🔍 AuthManager.loginBusinessOwner called with:', { username, password });
      
      // First check if already logged in with same credentials (must match Firebase session)
      const savedOwner = await this.getSavedBusinessOwner();
      const loginEmail = username.trim().toLowerCase();
      const ownerEmail = (savedOwner?.email || savedOwner?.username || '').trim().toLowerCase();
      if (
        savedOwner &&
        ownerEmail === loginEmail &&
        savedOwner.password === password
      ) {
        console.log('✅ Business owner already logged in');
        return savedOwner;
      }
      
      // Import test business owners dynamically to avoid circular dependency
      const { testBusinessOwners } = await import('./testBusinessOwners');
      console.log('🔍 Checking against test business owners...');
      console.log('🔍 Total test business owners:', testBusinessOwners.length);
      console.log('🔍 Test business owners:', testBusinessOwners.map(o => ({ username: o.username, email: o.email, companyName: o.companyName })));
      
      // Find matching business owner in test data
      console.log('🔍 Searching for owner with username/email:', username, 'and password:', password);
      
      const matchingOwner = testBusinessOwners.find(owner => {
        const usernameMatch = owner.username === username || owner.email === username;
        const passwordMatch = owner.password === password;
        console.log(`🔍 Checking owner ${owner.companyName}: usernameMatch=${usernameMatch}, passwordMatch=${passwordMatch}, owner.username=${owner.username}, owner.email=${owner.email}, owner.password=${owner.password}`);
        return usernameMatch && passwordMatch;
      });
      
      console.log('🔍 Matching owner search result:', matchingOwner ? 'FOUND' : 'NOT FOUND');
      if (matchingOwner) {
        console.log('🔍 Found owner details:', { username: matchingOwner.username, email: matchingOwner.email, companyName: matchingOwner.companyName });
      }
      
      if (matchingOwner) {
        console.log('✅ Found matching business owner:', matchingOwner.companyName);
        await this.saveBusinessOwner(matchingOwner);
        return matchingOwner;
      }
      
      console.log('❌ No matching business owner found');
      return null;
    } catch (error) {
      console.error('Error logging in business owner:', error);
      return null;
    }
  }

  // Check if user is logged in
  static async isUserLoggedIn(): Promise<boolean> {
    const user = await this.getSavedUser();
    return user !== null;
  }

  // Check if business owner is logged in
  static async isBusinessOwnerLoggedIn(): Promise<boolean> {
    const owner = await this.getSavedBusinessOwner();
    return owner !== null;
  }

  // Logout user (clear session but keep account data)
  static async logoutUser(): Promise<void> {
    // Clear cache on logout
    this.clearUserCache();
    try {
      // Don't remove the user data, just clear the session
      // The user account should remain for future logins
      console.log('User logged out - account data preserved');
    } catch (error) {
      console.error('Error logging out user:', error);
    }
  }

  // Logout business owner (clear session but keep account data)
  static async logoutBusinessOwner(): Promise<void> {
    // Clear cache on logout
    this.clearUserCache();
    try {
      // Don't remove the business owner data, just clear the session
      // The business owner account should remain for future logins
      console.log('Business owner logged out - account data preserved');
    } catch (error) {
      console.error('Error logging out business owner:', error);
    }
  }

  // Clear all auth data
  static async clearAllAuth(): Promise<void> {
    try {
      await SimpleStorage.multiRemove([USER_STORAGE_KEY, BUSINESS_OWNER_STORAGE_KEY]);
    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  }
}

